import { useState, useEffect } from 'react'
import {
  TextInputField,
  InputSelectControlField,
  TextAreaField,
  Button,
  useNotifications,
  SimpleModal,
  Box,
  Flex
} from '@sequencehq/core-components'
import { systemPrompt } from '../ai/systemPromptV0'

const GEMINI_API_KEY_STORAGE_KEY = 'sequence-gemini-api-key'
const GEMINI_MODEL_STORAGE_KEY = 'sequence-gemini-model'
const GEMINI_SYSTEM_PROMPT_STORAGE_KEY = 'sequence-gemini-system-prompt-v2'

const DEFAULT_GEMINI_MODEL = 'gemini-3.5-flash'

const GEMINI_MODELS = [
  {
    value: DEFAULT_GEMINI_MODEL,
    label: 'Gemini 3.5 Flash',
    secondaryLabel: 'Latest, fast and versatile'
  },
  {
    value: 'gemini-3.1-pro-preview',
    label: 'Gemini 3.1 Pro (preview)',
    secondaryLabel: 'Enhanced reasoning and understanding'
  },
  {
    value: 'gemini-3.1-flash-lite',
    label: 'Gemini 3.1 Flash Lite',
    secondaryLabel: 'Fastest and cheapest'
  }
]

const isSupportedGeminiModel = (model: string): boolean =>
  GEMINI_MODELS.some(option => option.value === model)

const readGeminiModel = (): string => {
  const saved = localStorage.getItem(GEMINI_MODEL_STORAGE_KEY)
  if (saved && isSupportedGeminiModel(saved)) {
    return saved
  }
  if (saved) {
    localStorage.setItem(GEMINI_MODEL_STORAGE_KEY, DEFAULT_GEMINI_MODEL)
  }
  return DEFAULT_GEMINI_MODEL
}

interface ApiKeySettingsModalProps {
  isOpen: boolean
  onClose: () => void
}
// This is a temporary measure before we get actual backend support for AI API keys
// It's a scrappy way to be able to test the AI chat and some system prompts
export const AiApiKeySettingsModal = ({
  isOpen,
  onClose
}: ApiKeySettingsModalProps) => {
  const [apiKey, setApiKey] = useState('')
  const [selectedModel, setSelectedModel] = useState(DEFAULT_GEMINI_MODEL)
  const [customSystemPrompt, setCustomSystemPrompt] = useState('')
  const { displayNotification } = useNotifications()

  // Load existing settings from localStorage on mount
  useEffect(() => {
    if (isOpen) {
      const savedKey = localStorage.getItem(GEMINI_API_KEY_STORAGE_KEY)
      const savedPrompt = localStorage.getItem(GEMINI_SYSTEM_PROMPT_STORAGE_KEY)

      if (savedKey) {
        setApiKey(savedKey)
      }

      setSelectedModel(readGeminiModel())

      if (savedPrompt) {
        setCustomSystemPrompt(savedPrompt)
      } else {
        setCustomSystemPrompt(systemPrompt)
      }
    }
  }, [isOpen])

  const handleSave = () => {
    try {
      if (apiKey.trim()) {
        localStorage.setItem(GEMINI_API_KEY_STORAGE_KEY, apiKey.trim())
        displayNotification('API key saved', { type: 'success' })
      } else {
        localStorage.removeItem(GEMINI_API_KEY_STORAGE_KEY)
        displayNotification('API key cleared', { type: 'info' })
      }

      localStorage.setItem(GEMINI_MODEL_STORAGE_KEY, selectedModel)

      localStorage.setItem(
        GEMINI_SYSTEM_PROMPT_STORAGE_KEY,
        customSystemPrompt.trim()
      )

      onClose()
    } catch {
      displayNotification('Error saving settings', { type: 'error' })
    }

    return true
  }

  const handleCopyPrompt = async () => {
    try {
      await navigator.clipboard.writeText(customSystemPrompt)
      displayNotification('System prompt copied to clipboard', {
        type: 'success'
      })
    } catch {
      displayNotification('Failed to copy to clipboard', { type: 'error' })
    }
  }

  const handleResetPrompt = () => {
    setCustomSystemPrompt(systemPrompt)
    displayNotification('System prompt reset to default', { type: 'info' })
  }

  return (
    <SimpleModal
      isOpen={isOpen}
      onClose={onClose}
      title="AI API key settings"
      onSubmit={handleSave}
      minHeight="10px"
      data-testid="revrec.aiChat.apiKeySettings.modal"
    >
      <Flex flexDirection="column" gap="16px">
        <TextInputField
          label="Gemini API Key"
          type="password"
          placeholder="Enter your Gemini API key..."
          value={apiKey}
          onChange={e => setApiKey(e.target.value)}
          autoComplete="off"
        />
        <InputSelectControlField
          label="Gemini Model"
          placeholder="Select a model..."
          value={selectedModel}
          onChange={setSelectedModel}
          options={GEMINI_MODELS}
        />
        <Box>
          <TextAreaField
            label="System Prompt"
            placeholder="Enter custom system prompt..."
            value={customSystemPrompt}
            onChange={setCustomSystemPrompt}
            minHeight="200px"
            fontSize="12px"
            fontFamily="monospace"
          />
          <Flex gap="8px" mt="8px" maxHeight="40px" justifyContent="flex-end">
            <Button variant="ghost" onClick={handleCopyPrompt}>
              Copy
            </Button>
            <Button variant="ghost" onClick={handleResetPrompt}>
              Reset
            </Button>
          </Flex>
        </Box>
      </Flex>
    </SimpleModal>
  )
}

export const getGeminiApiKey = (): string | null => {
  try {
    return localStorage.getItem(GEMINI_API_KEY_STORAGE_KEY)
  } catch {
    return null
  }
}

export const getGeminiModel = (): string => {
  try {
    return readGeminiModel()
  } catch {
    return DEFAULT_GEMINI_MODEL
  }
}

export const getGeminiSystemPrompt = (): string => {
  try {
    const saved = localStorage.getItem(GEMINI_SYSTEM_PROMPT_STORAGE_KEY)
    return saved || systemPrompt
  } catch {
    return systemPrompt
  }
}
